// src/components/Map/MapView.js
import React, { useRef, useEffect, useImperativeHandle } from "react";
import { initMap } from "./MapInit";
import { STEPS } from "../../workflow/steps";
import { mastLayouts } from "../../config/mastlayouts";
import { FieldRectangleTool } from "../../drawing/tools/FieldRectangleTool";
import { MastRectangleTool } from "../../drawing/tools/MastRectangleTool";
import { PointTool } from "../../drawing/tools/PointTool";
import { BuildingsPolyLineTool } from "../../drawing/tools/BuildingsPolyLineTool";
import { renderProject, renderCalculationGrid, removeCalculationGrid } from "./MapProjectRenderer";
import { handleMapClick, handleMapMouseMove, handleUndo, activateCurrentTool, resetAllTools } from "./MapStepRenderer";
import { initGeometryLayers, renderGeometries } from "./MapGeometryRenderer";
import { renderFacadeLabels } from "./MapFacadeLabels";
import WaybackControl from "./WaybackControl";
import proj4 from "proj4";
import { getUtmProj } from "../../drawing/utils/projection";

const MapView = React.forwardRef(({
  workflowStep,
  geometries,
  setGeometries,
  firstABVector,
  setFirstABVector,
  firstUTM,
  setFirstUTM,
  setWorkflowStep,
  selectedLayout,
  activeProject,
  mapMode = "draw", // could be "view"
  // PRD_followups.md (2026-05-20): when navigating from CreateProjectPage
  // → SketchPage the user already centred / zoomed the preview map on
  // the sportplatz; pass that view in so the wizard map opens at the
  // same place instead of the hard-coded fallback.
  initialMapView = null,
  // „Skizze bearbeiten" (07.08.2026): Eckpunkte, auf die die Karte beim
  // Start eingepasst wird — [[lonMin, latMin], [lonMax, latMax]]. Ein fester
  // Zoom passt nicht, weil Spielfelder unterschiedlich groß sind: bei Zoom 18
  // ragt ein normales Fußballfeld aus dem Bild. Nur beim Laden einer
  // gespeicherten Skizze gesetzt; sonst bleibt es beim initialMapView.
  initialBounds = null,
  // Kantenraster der geladenen Skizze: je Lichtpunkt {edge, t, angle}.
  // Wird an das Mastwerkzeug durchgereicht (Schiene + Pfeilrichtung).
  initialRaster = null,
}, ref) => {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const workflowStepRef = useRef(workflowStep);
  const selectedLayoutRef = useRef(selectedLayout);
  const geometriesRef = useRef(geometries);
  const firstABVectorRef = useRef(firstABVector);

  const tools = useRef({}).current;

  // „Symmetrieren" (Philips Wunsch 25.08.2026): Original-Lage des
  // gezeichneten Mastrechtecks, damit das Abhaken der Checkbox die
  // Verschiebung wieder zuruecknehmen kann.
  const mastRectVorSymmetrieRef = useRef(null);

  useEffect(() => { workflowStepRef.current = workflowStep; }, [workflowStep]);
  useEffect(() => { selectedLayoutRef.current = selectedLayout; }, [selectedLayout]);
  useEffect(() => { geometriesRef.current = geometries; }, [geometries]);
  useEffect(() => { firstABVectorRef.current = firstABVector; }, [firstABVector]);

  // ── Geladene Skizze an das Mast-Werkzeug übergeben (07.08.2026) ──────
  // Bei „Skizze bearbeiten" kommen Feld, Masten und Pfeile aus der
  // Datenbank in den State, ohne dass jemand gezeichnet hat — das
  // PointTool hat dann kein Raster und könnte die Pfeile beim Verschieben
  // eines Masten nicht mitführen. Wir reichen die geladene Skizze einmal
  // an, sobald beides da ist (Werkzeuge UND Geometrie).
  //
  // Die Bedingung `!tools.pointTool.rect` sorgt dafür, dass das im
  // normalen Zeichen-Ablauf NIE feuert: dort setzt placePoints das Raster,
  // bevor es überhaupt Mastpunkte im State gibt.
  const maybeRestoreSketch = (g) => {
    if (!tools.pointTool || tools.pointTool.rect) return;
    if (!g?.fieldRectangle || !g?.mastPoints?.length || !g?.mastArrows?.length) return;
    // Bezugsrechteck ist das Mast-Rechteck, wenn SketchPage es zurückrechnen
    // konnte — sonst das Spielfeld als Ersatz-Anker.
    //
    // Das Kantenraster kommt fertig von SketchPage (Preset oder aus der
    // Geometrie gemessen) — ohne wüsste das Werkzeug nicht, auf welcher
    // Kante ein Mast sitzt, und die Schiene würde ihn beim Ziehen auf die
    // falsche Seite projizieren. Nur zusammen mit einem echten
    // Mast-Rechteck weitergeben; gegen das Spielfeld gerechnet läge die
    // Schiene auf der falschen Linie.
    const raster = g.mastsRectangle ? initialRaster : null;
    const art = tools.pointTool.restoreFromSketch(
      g.mastsRectangle || g.fieldRectangle, g.mastPoints, g.mastArrows, raster
    );
    // Bei bekanntem Preset wurde die Ausrichtung nach der heutigen Regel neu
    // bestimmt (Feldmitte + Layout-Versatz) — dann die Pfeile auch neu
    // zeichnen, sonst zeigt die Karte weiter die alten aus der Datenbank.
    if (art === "preset") tools.pointTool.placeArrows(g.mastPoints);
  };

  useEffect(() => {
    if (mapRef.current) return;

    mapRef.current = initMap(mapContainer, initialMapView);

    mapRef.current.on("load", () => {

      initGeometryLayers(mapRef.current);

      tools.fieldTool = new FieldRectangleTool(mapRef.current, setGeometries, setWorkflowStep, firstABVectorRef);
      tools.pointTool = new PointTool(mapRef.current, () => firstABVectorRef.current, () => geometriesRef.current, setGeometries);
      tools.mastTool = new MastRectangleTool(
        mapRef.current,
        setGeometries,
        setWorkflowStep,
        () => selectedLayoutRef.current ? mastLayouts.find(l => l.id === selectedLayoutRef.current) : null,
        tools.pointTool,
        null
      );
      // getGeometries wird fuer die Hoehen-Vorschau gebraucht: die Bezugshoehe
      // ist das gezeichnete Spielfeld (geometries.fieldRectangle).
      tools.polylineTool = new BuildingsPolyLineTool(
        mapRef.current,
        setGeometries,
        () => geometriesRef.current,
      );

      mapRef.current.on("click", (e) => handleMapClick({ e, workflowStepRef, tools, mapMode }));
      mapRef.current.on("mousemove", (e) => handleMapMouseMove({ e, workflowStepRef, tools, mapMode }));

      // Sicherheitsnetz: baut MapLibre den Stil noch einmal auf, sind Quelle
      // und Zeichen-Ebenen weg — und da sich `geometries` danach nicht mehr
      // ändert, bliebe die Karte bei einer geladenen Skizze dauerhaft leer.
      // Passiert im Alltag beim Hot-Reload des Sandbox-Dev-Servers und wenn
      // der Wayback-Regler die Luftbild-Quelle austauscht. Kostet nichts:
      // solange die Quelle steht, tut der Block gar nichts.
      mapRef.current.on("styledata", () => {
        // Rastervorschau zuerst und mit eigener Prüfung: sie hat eine
        // andere Quelle als die Zeichnung — steht "final-geometries"
        // noch, kann die Vorschau trotzdem weggefegt worden sein.
        // __calcGridVisible unterscheidet "Stil hat den Layer gefressen"
        // (wiederherstellen) von "Nutzer hat ausgeblendet": auch das
        // Ausblenden feuert styledata, und ohne das Flag würde der
        // Handler das Raster sofort wieder anlegen — der Haken in der
        // Sidebar wäre wirkungslos (Philips Fund, 13.08.).
        if (
          mapRef.current.__calcGridData &&
          mapRef.current.__calcGridVisible &&
          !mapRef.current.getSource("calc-grid")
        ) {
          renderCalculationGrid(mapRef.current);
        }
        if (mapRef.current.getSource("final-geometries")) return;
        initGeometryLayers(mapRef.current);
        renderGeometries(mapRef.current, geometriesRef.current);
      });

      // Falls die Karte erst FERTIG wird, nachdem die Skizze schon im State
      // liegt („Skizze bearbeiten"): hier nachziehen. Der Effekt unten
      // greift nur bei Änderungen von `geometries` und liefe sonst ins Leere.
      maybeRestoreSketch(geometriesRef.current);

      // Und die geladene Skizze EINMAL zeichnen. Ohne das bleibt die Karte
      // beim Bearbeiten leer: der [geometries]-Effekt feuert beim Mounten
      // zwar, aber da gibt es die Quelle "final-geometries" noch nicht
      // (initGeometryLayers lief erst eben) — renderGeometries steigt dann
      // still aus, und danach ändert sich `geometries` nicht mehr. Im
      // normalen Zeichen-Ablauf fällt das nie auf, weil dort jeder Klick
      // nach dem Kartenstart den State ändert.
      renderGeometries(mapRef.current, geometriesRef.current);
      renderFacadeLabels(mapRef.current, geometriesRef.current?.buildingPolylines);
      if (geometriesRef.current?.fieldRectangle) {
        tools.mastTool?.setReferenceFromFieldRectangle(
          geometriesRef.current.fieldRectangle
        );
      }

      // Ausschnitt auf die geladene Skizze einpassen (statt festem Zoom, denn
      // Feldgrößen unterscheiden sich und die Masten stehen außerhalb).
      // Erst bei "idle", also nach dem ersten fertigen Frame: vorher steht
      // die Größe des Flex-Containers noch nicht fest, fitBounds würde gegen
      // eine halbfertige Fläche rechnen. resize() davor, damit die
      // Kartenfläche sicher gemessen ist.
      if (initialBounds) {
        let eingepasst = false;
        const einpassen = () => {
          if (eingepasst || !mapRef.current) return;
          eingepasst = true;
          try {
            mapRef.current.resize();
            mapRef.current.fitBounds(initialBounds, {
              padding: 70, animate: false, maxZoom: 19,
            });
          } catch (e) {
            // Lieber der Startausschnitt aus initialMapView als eine leere
            // Karte — die Skizze ist ja schon gezeichnet.
            console.warn("fitBounds auf die geladene Skizze fehlgeschlagen", e);
          }
        };
        mapRef.current.once("idle", einpassen);
        // Reissleine: in einem Hintergrund-Tab drosselt der Browser die
        // Bildschirmaktualisierung, dann kommt "idle" womoeglich nie. Nach
        // einer Sekunde passen wir notfalls ohne dieses Signal ein.
        setTimeout(einpassen, 1000);
      }

      // Debug-Zugang NUR im Dev-Build (npm start): erlaubt automatisierten
      // UI-Tests, Karte und Zeichenwerkzeuge direkt anzusprechen. Im
      // Produktions-Build (npm run build) ist der Block wegkompiliert.
      if (process.env.NODE_ENV !== "production") {
        window.__ilsDebug = {
          map: mapRef.current, tools, workflowStepRef, geometriesRef,
          // Rastervorschau direkt ansteuerbar (UI-Tests): erspart den
          // vollen Berechnungslauf, den der normale Weg über die Sidebar
          // braucht, um an Punkte zu kommen.
          showCalculationGrid: (pts) => renderCalculationGrid(mapRef.current, pts),
          hideCalculationGrid: (keepData) => removeCalculationGrid(mapRef.current, keepData),
        };
      }
    });
  }, []);

  // ── Strg/Cmd+Z → Undo im aktiven Zeichen-Schritt ────────────────────
  // (Signify-Feedback Dennis 03.07.2026, ergänzt den Button unten.)
  // Eingabefelder (Adresssuche, Masthöhe, …) behalten ihr eigenes Undo.
  useEffect(() => {
    if (mapMode !== "draw") return;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== "z") return;
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      handleUndo({ workflowStepRef, tools });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;

    renderGeometries(mapRef.current, geometries);
    // Nummern "1", "2", … mittig auf jeder Fassade — passend zur
    // Fassaden-Tabelle in der Sidebar.
    renderFacadeLabels(mapRef.current, geometries.buildingPolylines);

    if (geometries.fieldRectangle) {
      tools.mastTool?.setReferenceFromFieldRectangle(
        geometries.fieldRectangle
      );
    }

    maybeRestoreSketch(geometries);
  }, [geometries]);

  useEffect(() => {
    if (!mapRef.current || !activeProject) return;

    resetAllTools(tools);
    setGeometries({ fieldRectangle: null, mastsRectangle: null, fieldCenterLine: null, mastArrows: [], mastPoints: [], buildingPolylines: [], buildingHeights: [], polylines: [] });
    setWorkflowStep(STEPS.INIT);
    renderProject(mapRef.current, activeProject);
  }, [activeProject]);

  const resetMap = () => {
    if (!mapRef.current) return;
    resetAllTools(tools);
    // Rastervorschau komplett vergessen — eine neue Skizze hat mit dem
    // alten Ergebnis nichts mehr zu tun.
    removeCalculationGrid(mapRef.current);
    setGeometries({ fieldRectangle: null, mastsRectangle: null, fieldCenterLine: null, mastArrows: [], mastPoints: [], buildingPolylines: [], buildingHeights: [], polylines: [] });
  };

  // Expose map actions to parent (App.js) and siblings (Sidebar)
  useImperativeHandle(ref, () => ({
    resetMap,
    // Fly the map camera to a specific location — used by address search
    flyTo: (lng, lat, zoom = 17) => {
      if (mapRef.current) {
        mapRef.current.flyTo({ center: [lng, lat], zoom, duration: 2000 });
      }
    },
    // Undo des letzten Zeichen-Klicks (Signify-Feedback Dennis 03.07.2026).
    // Der Button dazu sitzt in der Sidebar (unter der Überschrift) und ruft
    // das hier über mapRef auf — die Werkzeuge leben nun mal in MapView.
    undo: () => handleUndo({ workflowStepRef, tools }),
    // X-Knopf je Zeile in der Fassaden-Tabelle. Muss ueber das Werkzeug
    // laufen und nicht direkt ueber setGeometries: der Zeichner haelt seine
    // eigene `lines`-Liste, die sonst auseinanderliefe.
    deleteBuilding: (index) => tools.polylineTool?.removeLine(index) || false,
    // Alle Fassaden verwerfen (Sidebar beim Zuruecksprung auf Schritt 5).
    clearBuildings: () => tools.polylineTool?.clear(),
    // Rastervorschau nach "Calculation complete" (2026-08-12): die Sidebar
    // holt GET /projects/:id und reicht die calculationpoints hierher —
    // gleiche Mechanik wie undo/deleteBuilding, die Karte lebt nun mal hier.
    showCalculationGrid: (calcPoints) => {
      if (mapRef.current) renderCalculationGrid(mapRef.current, calcPoints);
    },
    // keepData: der Toggle in der Sidebar blendet nur aus — beim erneuten
    // Einblenden kommen die Punkte aus map.__calcGridData, ohne neuen Fetch.
    hideCalculationGrid: (keepData = true) => {
      if (mapRef.current) removeCalculationGrid(mapRef.current, keepData);
    },
    // „Symmetrieren" (Philips Wunsch 25.08.2026, aus den Relux-Dateien:
    // dort wird meist symmetrisch geplant): schiebt das gezeichnete
    // Mastrechteck so, dass sein Mittelpunkt auf der SPIELFELD-Mitte
    // liegt — Masten und Pfeile werden neu platziert und stehen damit
    // punktsymmetrisch zur Feldmitte (die t-Raster der Presets sind
    // symmetrisch). Nur eine Verschiebung: Seitenlaengen und Ausrichtung
    // bleiben, wie gezeichnet. enabled=false stellt die Original-Lage
    // wieder her. Gibt true zurueck, wenn umgeschaltet wurde — haendisch
    // verschobene Masten werden dabei zurueckgesetzt (deshalb sitzt die
    // Checkbox VOR dem Mast-Editieren im Ablauf).
    // layoutObj: die Sidebar reicht das aufgeloeste Layout mit — bei einer
    // geladenen Skizze ohne Preset (layoutOverride) steht es nicht in
    // mastLayouts und waere hier sonst nicht auffindbar.
    symmetrizeMastRect: (enabled, layoutObj = null) => {
      const g = geometriesRef.current;
      const layoutId = selectedLayoutRef.current;
      const layout =
        layoutObj || (layoutId ? mastLayouts.find((l) => l.id === layoutId) : null);
      if (!g?.fieldRectangle || !g?.mastsRectangle || !layout || !tools.pointTool) {
        return false;
      }

      let zielRect;
      if (enabled) {
        mastRectVorSymmetrieRef.current = g.mastsRectangle;
        const wgs84 = "EPSG:4326";
        const utm = getUtmProj(g.fieldRectangle[0]);
        // Eckenmittel = Rechteckmitte; einen geschlossenen Ring vorher
        // oeffnen, sonst zieht der Duplikatpunkt den Mittelwert zur Startecke.
        const mitte = (poly) => {
          const pts = poly.map((p) => proj4(wgs84, utm, p));
          const ring =
            pts.length > 4 &&
            Math.hypot(pts[0][0] - pts[pts.length - 1][0], pts[0][1] - pts[pts.length - 1][1]) < 1e-9
              ? pts.slice(0, -1)
              : pts;
          const s = ring.reduce((a, p) => [a[0] + p[0], a[1] + p[1]], [0, 0]);
          return [s[0] / ring.length, s[1] / ring.length];
        };
        const feldMitte = mitte(g.fieldRectangle);
        const mastMitte = mitte(g.mastsRectangle);
        const dx = feldMitte[0] - mastMitte[0];
        const dy = feldMitte[1] - mastMitte[1];
        zielRect = g.mastsRectangle.map((p) => {
          const q = proj4(wgs84, utm, p);
          return proj4(utm, wgs84, [q[0] + dx, q[1] + dy]);
        });
      } else {
        zielRect = mastRectVorSymmetrieRef.current;
        if (!zielRect) return false;
      }

      // Masten + Pfeile neu platzieren — gleiche Mechanik wie beim
      // Zeichnen (MastRectangleTool.finalizeRectangle).
      const layoutPoints = layout.edges.map((edge, i) => ({
        edge,
        t: layout.t[i],
        angle: layout.angles[i],
      }));
      tools.pointTool.placePoints(zielRect, layoutPoints);
      setGeometries((prev) => ({ ...prev, mastsRectangle: zielRect }));
      return true;
    },
  }));

  // Relativer Wrapper, damit der Wayback-Slider (absolute) über der Karte
  // schwebt; .map füllt den Wrapper wie bisher den .map-wrapper (flex).
  return (
    <div style={{ position: "relative", flex: 1, minHeight: 0, display: "flex" }}>
      <div ref={mapContainer} className="map" />
      {/* Aufnahmedatum des Satellitenbilds wählen (Esri Wayback) —
          reine Zeichenhilfe, keine Auswirkung auf die Berechnung. */}
      <WaybackControl mapRef={mapRef} />
    </div>
  );
});

export default MapView;